import { CartGroupedSections, CartUpdateEvent, ThemeEvents } from "@theme/events";
import { morphSection } from "@theme/section-renderer";
import { fetchConfig } from "@theme/utilities";

/**
 * Free gifts, ported from the Mobile Pixels theme.
 *
 * Gifts are configured by the Shuffle Life Services app, which stores a map in
 * the shop-level `free_gift/config` JSON metafield keyed by trigger product ID:
 * `{ "<productId>": [{ id: "<gift variant ID>", productId, handle, enabled }] }`.
 * Entries with `enabled: false` are paused campaigns (toggled in bulk from the
 * app's Free Gifts list page) and are treated as nonexistent here; a missing
 * `enabled` means enabled (legacy data). Leftover marked gift lines of a paused
 * campaign reconcile to a target of zero and are reclaimed on the next sync.
 * The metafield value is exposed as `window.FoxTheme.settings.freeGifts` in
 * scripts.liquid, which also loads this module only when the metafield is set.
 *
 * Each gift variant's target quantity is the summed cart quantity of every
 * trigger product mapped to it (A x2 + B x3 both gifting C -> C x5). The cart
 * is reconciled both ways: missing gift quantity is added, and gift lines are
 * reduced or removed when their triggers shrink or leave the cart. Gift lines
 * carry the hidden `_free_gift` line item property; only marked lines are ever
 * touched, customer-added lines never are. Making the gift free is NOT handled
 * here — a Buy X Get Y automatic discount configured in the Shopify admin does
 * that; this module only adds and removes the products. The checkout is
 * covered separately by the app's checkout UI extension, which runs the same
 * reconciliation there.
 */
const FreeGift = {
  _busy: false,
  _pending: false,
  /** @type {Record<string, number>} Per-variant target that last failed (e.g. sold out); not retried until the target changes. */
  _failedTargets: {},

  _config() {
    const config = window.FoxTheme?.settings?.freeGifts;
    return config && typeof config === "object" ? config : {};
  },

  _isGiftLine(item) {
    return Boolean(item.properties && item.properties._free_gift);
  },

  /**
   * The rendered cart rows of a gift line, across every cart surface currently
   * mounted (drawer and cart page can both be in the DOM).
   *
   * A Shopify line key hashes the line's discount allocation state, so the moment
   * a trigger product leaves the cart the gift line loses its Buy X Get Y
   * discount and cart.js reports a new key — while the DOM still carries the key
   * it was rendered with. Exact matching therefore falls back to matching on the
   * variant ID, the only stable part of the key. That fallback can also match a
   * plain line of the same variant the customer added themselves; showing a
   * spinner there too is harmless, and only rows rendered as gift lines are
   * considered anyway. (cart-items.js hits the same instability for gift wrap.)
   *
   * @param {string} lineKey
   * @returns {HTMLElement[]}
   */
  _giftRowElements(lineKey) {
    const key = String(lineKey);
    const exact = document.querySelectorAll(`tr.cart-items__table-row--free-gift[data-key="${key}"]`);
    if (exact.length > 0) return [...exact];
    const variantId = key.split(":")[0];
    return [...document.querySelectorAll(`tr.cart-items__table-row--free-gift[data-key^="${variantId}:"]`)];
  },

  /**
   * Marks a gift line as syncing, filling the gap between the trigger line
   * re-rendering and the gift line following it. Reuses the theme's per-line busy
   * indicator: `btn--loading` on the row's remove button, which cart-items.js
   * also sets for a plain quantity change (both placements of the button are
   * covered — the drawer shows the one in the quantity cell, the cart page the
   * one in the remove column). Only lines already in the DOM can be marked; a
   * gift line being created has no row yet and appears with the final render.
   *
   * @param {string} lineKey
   */
  _markGiftRowLoading(lineKey) {
    this._giftRowElements(lineKey).forEach((row) => {
      row.dataset.giftSyncing = "true";
      row.querySelectorAll(".cart-items__remove-button").forEach((button) => button.classList.add("btn--loading"));
    });
  },

  /**
   * Clears the indicator from the rows this module marked once a sync pass
   * settles. The closing re-render in _notify() replaces the row markup anyway,
   * but a pass that failed (a sold out gift, say) never re-renders and would
   * otherwise spin forever. Keyed on our own marker attribute so a spinner
   * cart-items.js owns — a removal the customer started meanwhile — is left be.
   */
  _clearGiftRowsLoading() {
    document.querySelectorAll("tr[data-gift-syncing]").forEach((row) => {
      delete row.dataset.giftSyncing;
      row.querySelectorAll(".cart-items__remove-button").forEach((button) => button.classList.remove("btn--loading"));
    });
  },

  async sync() {
    if (this._busy) {
      this._pending = true;
      return;
    }
    this._busy = true;
    try {
      const config = this._config();
      if (Object.keys(config).length === 0) return;

      const cart = await (await fetch(FoxTheme.routes.cart)).json();
      const items = cart.items || [];

      // Total quantity per trigger product (variant lines of the same product
      // merged). Only marked gift lines are excluded, so an auto-added gift can
      // never count as a trigger and loop; a customer-added unit of a variant
      // that is also some campaign's gift still counts, matching the checkout
      // extension.
      const triggerQtyByProduct = {};
      for (const item of items) {
        if (this._isGiftLine(item)) continue;
        const productId = String(item.product_id);
        triggerQtyByProduct[productId] = (triggerQtyByProduct[productId] || 0) + item.quantity;
      }

      const desiredQty = {};
      for (const [triggerId, gifts] of Object.entries(config)) {
        const qty = triggerQtyByProduct[String(triggerId)] || 0;
        if (qty <= 0) continue;
        for (const gift of gifts || []) {
          if (!gift?.id || gift.enabled === false) continue;
          const variantId = String(gift.id);
          desiredQty[variantId] = (desiredQty[variantId] || 0) + qty;
        }
      }

      // Variants to reconcile: the desired gifts plus any marked lines already
      // in the cart (so gifts are reclaimed when their trigger leaves).
      const variantIds = new Set(Object.keys(desiredQty));
      for (const item of items) {
        if (this._isGiftLine(item)) variantIds.add(String(item.variant_id));
      }

      let changed = false;

      for (const variantId of variantIds) {
        const giftLines = items.filter((item) => String(item.variant_id) === variantId && this._isGiftLine(item));
        const plainQty = items
          .filter((item) => String(item.variant_id) === variantId && !this._isGiftLine(item))
          .reduce((sum, item) => sum + item.quantity, 0);
        // Customer-added quantity of the same variant counts toward the target
        // (customer already bought 1, 5 are due -> the gift line holds 4).
        const target = Math.max(0, (desiredQty[variantId] || 0) - plainQty);
        const currentQty = giftLines.reduce((sum, item) => sum + item.quantity, 0);

        if (currentQty === target) {
          delete this._failedTargets[variantId];
          continue;
        }

        try {
          if (currentQty < target) {
            if (this._failedTargets[variantId] === target) {
              continue;
            }
            if (giftLines.length > 0) {
              this._markGiftRowLoading(giftLines[0].key);
              await this._changeLine(giftLines[0].key, giftLines[0].quantity + (target - currentQty));
            } else {
              await this._addLine(variantId, target);
            }
          } else {
            // Reduce the first gift line; when several gift lines exist the
            // follow-up sync triggered by _notify picks up any remainder.
            const reducible = currentQty - target;
            this._markGiftRowLoading(giftLines[0].key);
            await this._changeLine(giftLines[0].key, Math.max(0, giftLines[0].quantity - reducible));
          }
          delete this._failedTargets[variantId];
          changed = true;
        } catch (error) {
          console.error("Free gift sync failed:", error);
          this._failedTargets[variantId] = target;
        }
      }

      if (changed) {
        await this._notify();
      }
    } finally {
      this._clearGiftRowsLoading();
      this._busy = false;
      if (this._pending) {
        this._pending = false;
        this.sync();
      }
    }
  },

  async _addLine(variantId, quantity) {
    const response = await fetch(
      FoxTheme.routes.cart_add_url,
      fetchConfig("json", {
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity, properties: { _free_gift: "true" } }],
        }),
      })
    );
    const parsed = await response.json();
    if (!response.ok || parsed.status) throw parsed;
  },

  async _changeLine(lineKey, quantity) {
    const response = await fetch(
      FoxTheme.routes.cart_change_url,
      fetchConfig("json", {
        body: JSON.stringify({ id: lineKey, quantity }),
      })
    );
    const parsed = await response.json();
    if (!response.ok || parsed.status) throw parsed;
  },

  /**
   * After silently mutating the cart, re-renders every registered cart section
   * in one round-trip and broadcasts a CartUpdateEvent so sibling surfaces
   * (cart count, free shipping goal, drawer contents) stay in sync. The event
   * re-triggers sync() through the cartUpdate listener; that pass finds nothing
   * to change and stops, so the loop converges.
   */
  async _notify() {
    const sectionIds = [];
    document.dispatchEvent(new CartGroupedSections(sectionIds));
    const uniqueSectionIds = [...new Set(sectionIds)];

    const response = await fetch(
      FoxTheme.routes.cart_update_url,
      fetchConfig("json", {
        body: JSON.stringify({
          updates: {},
          sections: uniqueSectionIds.join(","),
          sections_url: window.location.pathname,
        }),
      })
    );
    const cart = await response.json();

    let itemCount = null;
    if (cart.sections) {
      for (const sectionId of Object.keys(cart.sections)) {
        if (!cart.sections[sectionId]) continue;
        await morphSection(sectionId, cart.sections[sectionId]);
        const doc = new DOMParser().parseFromString(cart.sections[sectionId], "text/html");
        const countText = doc.querySelector('[ref="cartItemCount"]')?.textContent;
        const parsedCount = parseInt(countText ?? "", 10);
        if (!Number.isNaN(parsedCount)) itemCount = parsedCount;
      }
    }

    if (itemCount === null) {
      // No cart section on this page: mirror cart-item-count.liquid, which
      // excludes warranty lines from the visible count.
      const warrantyType = window.FoxTheme?.settings?.warrantyProductType;
      itemCount = (cart.items || []).reduce(
        (sum, item) => sum + (warrantyType && item.product_type === warrantyType ? 0 : item.quantity),
        0
      );
    }

    document.dispatchEvent(
      new CartUpdateEvent(cart, "free-gifts", {
        itemCount,
        source: "free-gifts",
        sections: cart.sections,
        // Reconciliation also runs on plain page loads; it must never pop the
        // cart drawer open on its own.
        suppressDrawerOpen: true,
      })
    );
  },
};

document.addEventListener(ThemeEvents.cartUpdate, () => FreeGift.sync());
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => FreeGift.sync());
} else {
  FreeGift.sync();
}
window.addEventListener("pageshow", (event) => {
  if (event.persisted) FreeGift.sync();
});
